import { getCampaignTranslations } from '@/redux/campaign/selectors';
import { connect } from 'react-redux';
import CSS from './Home.module.css';
import { M } from '@/utils/Motion';
import { useEffect, useState } from 'react';
import { LogoABZ, MCDLogo } from '@/assets/images';
import { useLoader, useDataLayer, pushEvent } from '@CS/hooks';
import RulesPopup from '@/components/Popups/RulesPopup';
import BaseButton from '@/components/BaseButton/BaseButton';
import { useNavigateQuery } from '@/hooks';
import HomeButton from '@/components/HomeButton';
import { getUserEntityData } from '@/redux/user/selectors';

type HomeProps = {
  translations: Record<string, string>;
  userData?: object;
};

const Home = ({ translations, userData }: HomeProps) => {
  const surveyComplete = (userData as { surveyComplete?: boolean } | undefined)?.surveyComplete;
  const showSurvey = !surveyComplete;

  const [rulesOpen, setRulesOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useDataLayer({
    event: 'page_view',
    eventProperties: {
      page_virtual_path: '/fcg26/intro',
      page_type: '/fam_card_game26'
    }
  });

  const splashLoader = useLoader(showSplash, { image: LogoABZ, opacity: 0, size: 300 });
  const navigate = useNavigateQuery();

  useEffect(() => {
    const minSplashMs = 700;
    const timer = window.setTimeout(() => setShowSplash(false), minSplashMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  if (showSplash) {
    return (
      <M.main className={CSS.loader}>
        <img src={MCDLogo} alt="McDonald's Logo" className={CSS.mcdLogo} />
        {splashLoader}
      </M.main>
    );
  }

  const onLetsGo = () => {
    pushEvent({
      event: 'button_click',
      eventAction: '/fcg26/letsgo',
      eventLabel: '/fcg26/1screengame'
    });
    navigate(showSurvey ? '/onboarding' : '/intro-ready');
  };

  return !Object.keys(translations).length ? null : (
    <M.main
      transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label="Home"
    >
      <section className="red-screen-container">
        <div className={CSS.startScreen}>
          <div className={CSS.header}>
            <img src={MCDLogo} alt="McDonald's Logo" className={CSS.mcdLogo} />
            <HomeButton style={{ position: 'absolute', right: 0, top: '-8px' }} />
          </div>
          <img src={LogoABZ} alt="Logo ABZ" className={CSS.abzLogo} />
          <h2 className={CSS.matchup}>
            <span>{translations.generalteamTrending}</span>
            <span className={CSS.vs}>{translations.startvs}</span>
            <span>{translations.generalteamTeletekst}</span>
          </h2>
          <p className={CSS.description}>{translations.startdescription}</p>
          <div className={CSS.actions}>
            <BaseButton
              text={translations.startrulesButton}
              variant="outline"
              onClick={() => {
                pushEvent({
                  event: 'button_click',
                  eventAction: '/fcg26/spelregels',
                  eventLabel: '/fcg26/1screengame'
                });
                setRulesOpen(true);
              }}
              disabled={!translations.startrulesButton}
            />
            <BaseButton
              text={translations.startletsGo}
              onClick={onLetsGo}
              disabled={!translations.startletsGo}
            />
          </div>

          {rulesOpen && (
            <RulesPopup 
              mode="home" 
              onClose={() => setRulesOpen(false)} 
              onPrimaryAction={onLetsGo} 
              translations={translations} 
            />
          )}
        </div>
      </section>
    </M.main>
  );
}

const mapStateToProps = (state: never) => ({
  translations: getCampaignTranslations(state),
  userData: getUserEntityData(state),
});

export default connect(mapStateToProps)(Home);
